import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import authAxios from '../utils/authAxios';
import StandardTableLayout from '../styles/StandardTableLayout';
import { SizeTag } from '../styles/CustomerSpecsIndexStyles';
import SecureImage from '../components/SecureImage';
import StatusPicker from '../components/StatusPicker';
import BagLabelModal, { BAGLABEL_FOLDER, toFilename } from '../components/BagLabelModal';
import BagLabelReplaceDialog from '../components/BagLabelReplaceDialog';
import { LightboxBackdrop, LightboxCloseButton } from '../styles/PackageIndexStyles';
import { revokeCachedUrl } from '../utils/imageCache';
import { BAG_LABEL_STATUSES, bagLabelStatusColor, normalizeBagLabelStatus } from '../utils/bagLabelStatus';
import { commodityColor, COMMODITY_NEUTRAL } from '../utils/commodityColor';

// Types gets an explicit share of the table so its one-line pill fit is driven by
// the viewport, not by how many pills happen to be rendered.
const columns = [
  { key: 'ImageLink', label: 'Image' },
  { key: 'LabelName', label: 'Label Name' },
  { key: 'Customers', label: 'Customers' },
  { key: 'Commodities', label: 'Commodities' },
  { key: 'Types', label: 'Types', width: '22%' },
  { key: 'Styles', label: 'Styles' },
  { key: 'Active', label: 'Status' },
];

// Image has no meaningful order; every other column sorts on its displayed text.
const SORTABLE = new Set(['LabelName', 'Customers', 'Commodities', 'Types', 'Styles', 'Active']);

// Landscape preview so label artwork is legible without opening the lightbox;
// `contain` keeps the uploaded aspect ratio instead of cropping it to a square.
const thumbStyle = {
  width: '168px',
  height: '92px',
  objectFit: 'contain',
  background: 'var(--white)',
  borderRadius: '6px',
  border: '1px solid var(--shadow-a08)',
  cursor: 'pointer',
  display: 'block',
};

const pillStyle = { background: 'var(--grey-600)', minWidth: 0, overflow: 'hidden' };

// Default pill paint. A column may pass `colorOf` to paint each pill with a
// data-keyed identity color instead — Commodities uses the app-wide commodity
// colorway so a pill here reads the same as on a Spec Index card.
const greyPaint = { background: 'var(--grey-600)' };
const paintFor = (colorOf, value) => {
  if (!colorOf) return greyPaint;
  const { bg, border } = colorOf(value);
  return { background: bg, border: `1px solid ${border}` };
};

const csvToList = (csv) => String(csv ?? '').split(',').map((t) => t.trim()).filter(Boolean);

// Sentinel-aware CSV -> displayed labels ('All'/'Any'/blank collapses to the sentinel).
const displayList = (csv, sentinel, sentinelLabel, nameOf) => {
  const s = String(csv ?? '').trim();
  if (!s || s === sentinel) return [sentinelLabel];
  return csvToList(s).map(nameOf);
};

const PlusButton = ({ label, ariaLabel, onClick, tone }) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    style={{
      height: 26, minWidth: 26, padding: '0 8px', borderRadius: 13, flex: '0 0 auto',
      background: tone, color: 'var(--white)', border: 'none',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 800, cursor: 'pointer',
    }}
  >
    {label}
  </button>
);

// Pill list that shows the first 4 values then a "+N" expander (a "−" collapses).
// No reusable component exists for this; local per Decision 13.
const CollapsedPills = ({ items, name, expanded, onExpand, onCollapse, colorOf }) => {
  const maxShown = 4;
  const visible = expanded ? items : items.slice(0, maxShown);
  const remaining = Math.max(0, items.length - maxShown);
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      {visible.map((v, i) => (
        <SizeTag key={`${i}-${v}`} style={paintFor(colorOf, v)}>{v}</SizeTag>
      ))}
      {remaining > 0 && !expanded ? (
        <PlusButton label={`+${remaining}`} ariaLabel={`Show all ${name}`} onClick={onExpand} tone="var(--green-800)" />
      ) : null}
      {expanded && items.length > maxShown ? (
        <PlusButton label="−" ariaLabel={`Show fewer ${name}`} onClick={onCollapse} tone="var(--grey-700)" />
      ) : null}
    </div>
  );
};

// Types cell: shows as many pills as the column can fit on ONE line (hard cap 8 —
// the practical maximum selection) and rolls the rest into a "+N" that expands
// (a "−" collapses). The fit is measured from the real pill widths, so a narrower
// viewport moves pills into the "+N" instead of wrapping the row taller.
const FitPills = ({ items, name, expanded, onExpand, onCollapse, colorOf, maxShown = 8 }) => {
  const capped = useMemo(() => items.slice(0, maxShown), [items, maxShown]);
  const wrapRef = useRef(null);
  const stripRef = useRef(null);
  const [fit, setFit] = useState(capped.length);
  const measureKey = capped.join('|');

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const strip = stripRef.current;
    if (!wrap || !strip) return undefined;
    const GAP = 6;
    const PLUS = 40; // "+N" chip plus the gap before it
    const recalc = () => {
      const widths = Array.from(strip.children).map((el) => el.getBoundingClientRect().width);
      const avail = wrap.getBoundingClientRect().width;
      if (!avail || !widths.length) return;
      const total = widths.reduce((sum, w) => sum + w, 0) + GAP * (widths.length - 1);
      if (total <= avail) { setFit(widths.length); return; }
      let used = 0;
      let shown = 0;
      for (const w of widths) {
        const next = used + w + (shown ? GAP : 0);
        if (next + PLUS > avail) break;
        used = next;
        shown += 1;
      }
      setFit(Math.max(1, shown));
    };
    recalc();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(recalc);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [measureKey]);

  const visible = expanded ? items : capped.slice(0, fit);
  const remaining = Math.max(0, items.length - fit);

  return (
    <div ref={wrapRef} style={{ position: 'relative', minWidth: 0 }}>
      {/* Off-layout copy of the pills, measured for their exact widths. */}
      <div
        ref={stripRef}
        aria-hidden="true"
        style={{
          position: 'absolute', top: 0, left: 0, display: 'flex', gap: 6,
          visibility: 'hidden', pointerEvents: 'none',
        }}
      >
        {capped.map((v, i) => (
          <SizeTag key={`m-${i}-${v}`} style={paintFor(colorOf, v)}>{v}</SizeTag>
        ))}
      </div>

      <div
        style={{
          display: 'flex', gap: 6, alignItems: 'center',
          flexWrap: expanded ? 'wrap' : 'nowrap', overflow: 'hidden',
        }}
      >
        {visible.map((v, i) => (
          <SizeTag key={`${i}-${v}`} style={{ ...pillStyle, ...paintFor(colorOf, v) }}>{v}</SizeTag>
        ))}
        {remaining > 0 && !expanded ? (
          <PlusButton label={`+${remaining}`} ariaLabel={`Show all ${name}`} onClick={onExpand} tone="var(--green-800)" />
        ) : null}
        {expanded && items.length > fit ? (
          <PlusButton label="−" ariaLabel={`Show fewer ${name}`} onClick={onCollapse} tone="var(--grey-700)" />
        ) : null}
      </div>
    </div>
  );
};

const BagTraceabilityPage = () => {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [packTypes, setPackTypes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState(null); // { key, direction: 'asc'|'desc' }
  const [modal, setModal] = useState({ open: false, mode: 'add', row: null });
  const [replaceDialog, setReplaceDialog] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  // Per-cell "+N" expansion, keyed `${row.UniqueID}:${colKey}`.
  const [pillsExpanded, setPillsExpanded] = useState(() => new Set());
  // Bumped after any image change so SecureImage thumbnails remount and re-probe.
  const [imgVersion, setImgVersion] = useState(0);

  const collator = useMemo(() => new Intl.Collator(undefined, { sensitivity: 'base' }), []);

  const fetchRows = useCallback(async () => {
    try {
      const res = await authAxios.get('/api/baglabels');
      setRows(res.data);
    } catch (e) { console.error('Fetch Bag Labels failed', e); }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await authAxios.get('/api/customers/dropdown');
      setCustomers(res.data);
    } catch (e) { console.error('Fetch Customers failed', e); }
  }, []);

  const fetchCommodities = useCallback(async () => {
    try {
      const res = await authAxios.get('/api/commodities/dropdown');
      setCommodities(res.data);
    } catch (e) { console.error('Fetch Commodities failed', e); }
  }, []);

  const fetchPackTypes = useCallback(async () => {
    try {
      const res = await authAxios.get('/api/packtypes');
      setPackTypes(res.data);
    } catch (e) { console.error('Fetch Pack Types failed', e); }
  }, []);

  useEffect(() => {
    fetchRows(); fetchCustomers(); fetchCommodities(); fetchPackTypes();
  }, [fetchRows, fetchCustomers, fetchCommodities, fetchPackTypes]);

  // Close the lightbox on Escape.
  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightboxSrc(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxSrc]);

  const customerNameById = useMemo(() => {
    const m = new Map();
    for (const c of customers) m.set(String(c.UniqueID), String(c.Customer || ''));
    return m;
  }, [customers]);

  const commodityNameById = useMemo(() => {
    const m = new Map();
    for (const c of commodities) m.set(String(c.UniqueID), String(c.Commodity || ''));
    return m;
  }, [commodities]);

  const styleNameById = useMemo(() => {
    const m = new Map();
    for (const r of packTypes) m.set(String(r.UniqueID), String(r.Style || ''));
    return m;
  }, [packTypes]);

  // Identity color for a rendered commodity pill; an unresolved id (`#12`) has no
  // name to key on, so it falls back to the neutral grey the sentinel pill uses.
  const commodityPaint = useCallback((displayName) => (
    commodityNameById.size && !String(displayName).startsWith('#')
      ? commodityColor(displayName)
      : COMMODITY_NEUTRAL
  ), [commodityNameById]);

  // Drop any cached blob object URL for a changed image so the SAS path doesn't serve stale bytes.
  const evictCache = (link) => {
    const f = toFilename(link);
    if (f) revokeCachedUrl(`${BAGLABEL_FOLDER}/${f}`);
  };

  const openAdd = () => setModal({ open: true, mode: 'add', row: null });
  const openEdit = (row) => setModal({ open: true, mode: 'edit', row });
  const closeModal = () => setModal((m) => ({ ...m, open: false }));

  const handleSaved = async ({ oldImageLink, newImageLink }) => {
    evictCache(oldImageLink);
    evictCache(newImageLink);
    setImgVersion((v) => v + 1);
    closeModal();
    await fetchRows();
  };

  const handleStatus = async (row, next) => {
    const isActive = next === 'Active';
    try {
      await authAxios.patch(`/api/baglabels/${row.UniqueID}/status`, { IsActive: isActive });
      setRows((prev) => prev.map((r) => (r.UniqueID === row.UniqueID ? { ...r, IsActive: isActive } : r)));
    } catch (e) {
      console.error('Bag label status update failed', e);
      alert('Could not update status.');
    }
  };

  // Pre-check dependents: pins get the replace dialog, unreferenced labels a plain confirm.
  const requestDelete = async (row) => {
    if (busy || !row) return;
    closeModal();
    setBusy(true);
    try {
      const { data: deps } = await authAxios.get(`/api/baglabels/${row.UniqueID}/dependents`);
      if ((deps?.specs?.count || 0) > 0) {
        setReplaceDialog({ open: true, row, dependents: deps });
        return;
      }
      if (!window.confirm(`Delete bag label "${row.LabelName}"?`)) return;
      try {
        await authAxios.delete(`/api/baglabels/${row.UniqueID}`);
      } catch (e) {
        // Dependents appeared between the pre-check and the DELETE — fall into the dialog.
        if (e?.response?.status === 409) {
          const { data: deps2 } = await authAxios.get(`/api/baglabels/${row.UniqueID}/dependents`);
          setReplaceDialog({ open: true, row, dependents: deps2 });
          return;
        }
        throw e;
      }
      evictCache(row.ImageLink);
      setImgVersion((v) => v + 1);
      fetchRows();
    } catch (e) {
      console.error('Bag label delete flow failed', e);
      alert('Could not delete bag label.');
    } finally {
      setBusy(false);
    }
  };

  // Delete a label that has dependents: rewrite their pins to `replaceWith` (a label id,
  // or null for "No bag label"), then delete — one transactional backend call.
  const confirmReplaceDelete = async (replaceWith) => {
    const row = replaceDialog?.row;
    if (!row) return;
    setBusy(true);
    try {
      await authAxios.post(`/api/baglabels/${row.UniqueID}/replace-delete`, { replaceWith });
      evictCache(row.ImageLink);
      setImgVersion((v) => v + 1);
      setReplaceDialog(null);
      fetchRows();
    } catch (e) {
      console.error('Bag label replace-delete failed', e);
      alert('Could not delete bag label.');
    } finally {
      setBusy(false);
    }
  };

  // What each sortable/searchable column actually shows, so sorting and search both
  // work on resolved names, sentinels and the status word — not on raw CSV ids.
  const cellText = useCallback((row) => ({
    LabelName: String(row?.LabelName ?? ''),
    Customers: displayList(row?.CustomersCsv, 'All', 'All Customers', (id) => customerNameById.get(id) || `#${id}`).join(', '),
    Commodities: displayList(row?.CommoditiesCsv, 'Any', 'Any', (id) => commodityNameById.get(id) || `#${id}`).join(', '),
    Types: displayList(row?.TypesCsv, 'Any', 'Any', (t) => t).join(', '),
    Styles: displayList(row?.StylesCsv, 'Any', 'Any', (id) => styleNameById.get(id) || `#${id}`).join(', '),
    Active: row?.IsActive ? 'Active' : 'Inactive',
  }), [customerNameById, commodityNameById, styleNameById]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => Object.values(cellText(r)).some((v) => v.toLowerCase().includes(q)));
  }, [rows, searchQuery, cellText]);

  // No sort selected = the default Label Name order.
  const sorted = useMemo(() => {
    const copy = [...filtered];
    if (!sortConfig) {
      return copy.sort((a, b) => collator.compare(a.LabelName ?? '', b.LabelName ?? ''));
    }
    const { key, direction } = sortConfig;
    return copy.sort((a, b) => {
      const cmp = collator.compare(cellText(a)[key] || '', cellText(b)[key] || '')
        || collator.compare(a.LabelName ?? '', b.LabelName ?? '');
      return direction === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortConfig, collator, cellText]);

  // A-Z -> Z-A -> back to the default order.
  const handleSort = (key) => {
    if (!SORTABLE.has(key)) return;
    setSortConfig((prev) => {
      if (prev?.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  };

  const isExpanded = (row, colKey) => pillsExpanded.has(`${row.UniqueID}:${colKey}`);
  const expand = (row, colKey) => setPillsExpanded((prev) => {
    const n = new Set(prev); n.add(`${row.UniqueID}:${colKey}`); return n;
  });
  const collapse = (row, colKey) => setPillsExpanded((prev) => {
    const n = new Set(prev); n.delete(`${row.UniqueID}:${colKey}`); return n;
  });

  const renderPillCell = (row, colKey, csv, sentinel, sentinelLabel, nameOf, ariaName, Component = CollapsedPills, colorOf) => {
    const s = String(csv ?? '').trim();
    if (!s || s === sentinel) {
      return <SizeTag style={{ background: 'var(--grey-600)' }}>{sentinelLabel}</SizeTag>;
    }
    return (
      <Component
        items={csvToList(s).map(nameOf)}
        name={ariaName}
        expanded={isExpanded(row, colKey)}
        onExpand={() => expand(row, colKey)}
        onCollapse={() => collapse(row, colKey)}
        colorOf={colorOf}
      />
    );
  };

  // Rows open the editor (Delete lives inside it). Inner controls — the image, the
  // "+N" chips and the StatusPicker — stop propagation so they still work on their own.
  const rowKeyDown = (e, row) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if (e.target !== e.currentTarget) return;
    e.preventDefault();
    openEdit(row);
  };

  return (
    <>
      <StandardTableLayout
        title="Bag Traceability"
        showForm={false}
        submitting={busy}
        onToggleForm={openAdd}
        onSubmit={(e) => e.preventDefault()}
        columns={columns}
        formData={{}}
        handleChange={() => {}}
        rows={sorted}
        editingId={null}
        hideActionsColumn
        sortConfig={sortConfig}
        onSort={handleSort}
        searchValue={searchQuery}
        onSearchChange={(e) => setSearchQuery(e.target.value)}
        getRowProps={(row, index) => ({
          onClick: () => openEdit(row),
          onKeyDown: (e) => rowKeyDown(e, row),
          tabIndex: 0,
          'aria-label': `Edit ${row?.LabelName || 'bag label'}`,
          style: { cursor: 'pointer' },
          $index: index,
        })}
        renderCell={(col, row) => {
          if (col === 'ImageLink') {
            const filename = toFilename(row?.ImageLink);
            if (!filename) return <span style={{ color: 'var(--ink-a45)' }}>—</span>;
            return (
              <SecureImage
                key={`thumb-${row.UniqueID}-${imgVersion}`}
                src={filename}
                folder={BAGLABEL_FOLDER}
                alt={row.LabelName}
                style={thumbStyle}
                title="Click to enlarge"
                onClick={(e) => { e.stopPropagation(); setLightboxSrc(filename); }}
              />
            );
          }
          if (col === 'LabelName') return row?.LabelName ?? '';
          if (col === 'Customers') {
            return renderPillCell(
              row, col, row?.CustomersCsv, 'All', 'All Customers',
              (id) => customerNameById.get(id) || `#${id}`, 'customers'
            );
          }
          if (col === 'Commodities') {
            return renderPillCell(
              row, col, row?.CommoditiesCsv, 'Any', 'Any',
              (id) => commodityNameById.get(id) || `#${id}`, 'commodities',
              CollapsedPills, commodityPaint
            );
          }
          if (col === 'Types') {
            return renderPillCell(row, col, row?.TypesCsv, 'Any', 'Any', (t) => t, 'types', FitPills);
          }
          if (col === 'Styles') {
            return renderPillCell(
              row, col, row?.StylesCsv, 'Any', 'Any',
              (id) => styleNameById.get(id) || `#${id}`, 'styles'
            );
          }
          if (col === 'Active') {
            return (
              <StatusPicker
                status={row?.IsActive ? 'Active' : 'Inactive'}
                confirm
                size="sm"
                statuses={BAG_LABEL_STATUSES}
                colorFor={bagLabelStatusColor}
                normalize={normalizeBagLabelStatus}
                ariaLabel={`Status for ${row?.LabelName || 'bag label'}`}
                onChange={(next) => handleStatus(row, next)}
              />
            );
          }
          return null;
        }}
      />

      <BagLabelModal
        open={modal.open}
        mode={modal.mode}
        row={modal.row}
        customers={customers}
        commodities={commodities}
        packTypes={packTypes}
        imgVersion={imgVersion}
        busy={busy}
        onClose={closeModal}
        onSaved={handleSaved}
        onRequestDelete={requestDelete}
      />

      <BagLabelReplaceDialog
        open={Boolean(replaceDialog?.open)}
        label={replaceDialog?.row}
        dependents={replaceDialog?.dependents}
        bagLabels={rows}
        busy={busy}
        onCancel={() => setReplaceDialog(null)}
        onConfirm={confirmReplaceDelete}
      />

      {lightboxSrc && (
        <LightboxBackdrop onClick={() => setLightboxSrc(null)} role="dialog" aria-modal="true">
          <LightboxCloseButton
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxSrc(null); }}
            aria-label="Close image"
          >
            ×
          </LightboxCloseButton>
          <div onClick={(e) => e.stopPropagation()}>
            <SecureImage
              key={`lightbox-${lightboxSrc}-${imgVersion}`}
              src={lightboxSrc}
              folder={BAGLABEL_FOLDER}
              alt="Bag label image"
              style={{ maxWidth: '92vw', maxHeight: '92vh', objectFit: 'contain', borderRadius: 12 }}
            />
          </div>
        </LightboxBackdrop>
      )}
    </>
  );
};

export default BagTraceabilityPage;
